import { useMemo, useState, useEffect } from 'react';
import { useAppStore } from '@/store';
import { Settings, MoreHorizontal } from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { Select } from 'calc-controls';
import { useAppLock } from '@/components/AppLock/AppLockContext';
import { LockMenu } from '@/components/AppLock/LockMenu';
import { DemoLoader } from './DemoLoader';
import styles from './Header.module.css';

export function Header() {
  const profiles = useAppStore((s) => s.profiles);
  const activeProfileId = useAppStore((s) => s.activeProfileId);
  const setActiveProfile = useAppStore((s) => s.setActiveProfile);
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const setSettingsOpen = useAppStore((s) => s.setSettingsOpen);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const profileOptions = useMemo(() => [
    { value: '', label: 'No profile' },
    ...profiles.map((p) => ({ value: p.id, label: p.name })),
  ], [profiles]);

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <Logo />
        <span className={styles.headerTitle}>Allergen Lens</span>
      </div>
      <div className={styles.headerControls}>
        {profiles.length > 0 && (
          <Select
            options={profileOptions}
            value={activeProfileId ?? ''}
            onChange={(v) => setActiveProfile(v || null)}
          />
        )}
        <button
          type="button"
          className={styles.menuToggle}
          aria-label="More controls"
          aria-expanded={menuOpen}
          aria-controls="header-secondary-controls"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <MoreHorizontal size={18} />
        </button>
        <div
          id="header-secondary-controls"
          className={`${styles.secondaryControls} ${menuOpen ? styles.secondaryOpen : ''}`}
        >
          <DemoLoader />
          {isEnabled && lock ? (
            <LockMenu lock={lock} />
          ) : enableEncryption ? (
            <button
              className={styles.settingsButton}
              onClick={enableEncryption}
              title="Enable password protection"
              aria-label="Enable password protection"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 019.9-1" />
              </svg>
            </button>
          ) : null}
          <ThemeToggle />
          <button
            className={styles.settingsButton}
            onClick={() => setSettingsOpen(true)}
            aria-label="Open settings"
            title="Settings"
          >
            <Settings size={18} />
          </button>
        </div>
        {menuOpen && (
          <button
            type="button"
            className={styles.menuBackdrop}
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setMenuOpen(false)}
          />
        )}
      </div>
    </header>
  );
}
